import { useState, type KeyboardEvent } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import editorCopy from '@/i18n/ns/workspace-editors';
import workspaceErrorsCopy from '@/i18n/ns/workspace-errors';
import { CompanyRequestError } from '@/company/api';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { OverflowMenu, type OverflowItem } from '@/components/ui/menu';
import { useOptionalWorkspace, useWorkspace, type WorkspaceState } from '../context';
import { useWorkspaceText } from '../interface-copy';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { emptyTurnContext } from '../types';
import { workspaceResultCanvasTarget } from '../artifact-panels/open-target';
import { projectsApi } from './api';
import { aboutOpened } from './about-state';
import { ProjectAddMenu } from './add-menu';
import { ProjectFaces, ProjectMark } from './mark';
import { ProjectShareButton } from './share';
import { chatProject, projectPlace, rememberProjectPlace } from './place';
import { itemKind, itemLine, personName, shortWhen, whereLabel } from './labels';
import {
  dropProject,
  projectWriter,
  putProject,
  refreshProject,
  useKnownProject,
  useProject,
  useProjectMissing,
} from './use-projects';
import type { ProjectDetail, ProjectItem, ProjectItemKind } from './types';

// Working inside a project (2026-10-16): a project's home is the chat home in the project.
// The same composer starts a chat that lives there, the project's name and brief sit above
// it, and its work sits below it where the home shows its feed.

type Workspace = WorkspaceState;

/** The project whose home is open: a new chat that starts in it, with nothing sent yet. */
export function projectHomeId(
  ws: Pick<WorkspaceState, 'view' | 'threadSaved' | 'turnContext' | 'projectId'>,
) {
  if (ws.view !== 'chat' || ws.threadSaved) return null;
  return ws.turnContext.projectId ?? ws.projectId ?? null;
}

function useProjectHome({ revalidate = false } = {}) {
  const ws = useWorkspace();
  const id = projectHomeId(ws);
  const state = useProject(id, { revalidate });
  // A project the reader can't open keeps no name here, not even one this tab saw before.
  return { id, place: state.missing ? null : projectPlace(id), ...state };
}

/** The project whose home the composer is on, by name ("New chat in Black Friday 2026"). */
export function useProjectHomeName(): string | null {
  const ws = useOptionalWorkspace();
  const id = ws ? projectHomeId(ws) : null;
  const known = useKnownProject(id);
  const missing = useProjectMissing(id);
  const name = known?.name ?? projectPlace(id)?.name;
  return name && !missing ? name : null;
}

/** Opens an item of the project where it lives: a chat in the conversation, the rest in the canvas. */
export async function openProjectItem(ws: Workspace, item: ProjectItem) {
  if (item.kind === 'chat') return ws.openThread(item.id);
  if (item.kind === 'result') {
    const target = await workspaceResultCanvasTarget({
      id: item.id,
      metadata: item.savedArtifact ? { savedArtifact: item.savedArtifact } : {},
    });
    if (target) ws.openCanvas(target);
    return;
  }
  ws.openCanvas({ kind: item.kind, id: item.id });
}

/** The project a file attached right now lands in: the open home's, or the open chat's. */
export function attachProject(ws: Workspace | null | undefined): string | null {
  if (!ws || ws.readOnly) return null;
  return projectHomeId(ws) ?? (ws.threadSaved ? (chatProject(ws)?.id ?? null) : null);
}

/**
 * A file attached in a project's chat, or on its home, joins that project as the results made
 * there do. Joining shares nothing: the file stays as private as it was.
 */
export function joinProject(
  projectId: string | null,
  file: { id: string; name: string },
  onFailed?: (message: string) => void,
) {
  if (!projectId) return;
  void projectsApi
    .addItem(projectId, { kind: 'file', id: file.id })
    .then(projectWriter())
    .catch((failure: unknown) => {
      // The project went away or isn't the person's to use any more: the file stays in the Library.
      if (failure instanceof CompanyRequestError && [403, 404].includes(failure.status)) return;
      // The catalog's own sentence, which ws.notify then words in the person's language.
      onFailed?.(formatCopy(workspaceErrorsCopy.en.projectFileJoinFailed, { name: file.name }));
    });
}

/** A chat that lives in the project, started with these words (sent, or left to finish). */
export function startProjectChat(
  ws: Workspace,
  project: { id: string; name: string; tint: ProjectDetail['tint'] },
  text: string,
  submit = true,
) {
  rememberProjectPlace({ id: project.id, name: project.name, tint: project.tint });
  ws.send(text, { ...emptyTurnContext(), projectId: project.id }, submit, null, { newChat: true });
}

/** "About this project": the side panel with the brief, its Skills, what it reads first and its people. */
export function AboutProjectButton({ id, inline = false }: { id: string; inline?: boolean }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const open = ws.canvas?.kind === 'project' && ws.canvas.id === id;
  // Open, the panel names itself and closes with its own ✕.
  if (open) return null;
  return (
    <button
      type="button"
      className={inline ? undefined : 'ws-project-about-link'}
      data-project-about={inline ? 'inline' : 'bar'}
      onClick={() => {
        if (ws.openCanvas({ kind: 'project', id })) aboutOpened(inline ? 'inline' : 'bar');
      }}
    >
      <span className="ws-project-about-long">{copy.aboutProject}</span>
      {/* One of the two shows; the shown words are the button's name (label in name). */}
      <span className="ws-project-about-short">{copy.aboutShort}</span>
      {inline && <WorkspaceIcon name="arrow" size={14} />}
    </button>
  );
}

/** Above the composer: where you are, the project's name and the brief every chat here reads. */
export function ProjectHomeWelcome() {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const { id, project, place, missing } = useProjectHome();
  if (!id) return null;
  if (missing)
    return (
      <div className="ws-welcome ws-project-home-head">
        <h1>{copy.unavailableTitle}</h1>
        <p>{copy.unavailableDescription}</p>
        <button type="button" className="ws-button" onClick={() => ws.openProject(null)}>
          {copy.allProjects}
        </button>
      </div>
    );
  const name = project?.name ?? place?.name ?? '';
  return (
    <div className="ws-welcome ws-project-home-head" data-testid="project-home">
      <div className="ws-company-context ws-project-context">
        <span className="ws-company-context-label">
          {name && (
            <ProjectMark name={name} tint={project?.tint ?? place?.tint ?? 'plain'} size="xs" />
          )}
          <span className="ws-project-context-name">{formatCopy(copy.workingIn, { name })}</span>
          {project && (
            <span className="ws-project-context-where">
              <span aria-hidden="true">·</span>
              {whereLabel(project.visibility, ws.companyName, locale)}
            </span>
          )}
        </span>
        <AboutProjectButton id={id} inline />
      </div>
      <h1>{name}</h1>
      {project &&
        (project.brief.trim() ? (
          <p className="ws-project-brief">{project.brief}</p>
        ) : (
          <p className="ws-project-brief">
            {project.role === 'viewer' ? copy.noBrief : copy.noBriefHome}
          </p>
        ))}
    </div>
  );
}

type Tab = 'all' | 'chats' | 'results' | 'files';
type ProjectCopy = typeof workspaceFeaturesCopy.en;
const TABS: {
  id: Tab;
  label: keyof ProjectCopy;
  empty: keyof ProjectCopy | null;
  kinds: ProjectItemKind[] | null;
}[] = [
  { id: 'all', label: 'tabAll', empty: null, kinds: null },
  { id: 'chats', label: 'tabChats', empty: 'emptyChats', kinds: ['chat'] },
  { id: 'results', label: 'tabResults', empty: 'emptyResults', kinds: ['result'] },
  { id: 'files', label: 'tabFiles', empty: 'emptyFiles', kinds: ['file', 'meeting'] },
];

/** Below the composer, where the home has its feed: the work in the project. */
export function ProjectWork() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const { text: uiText } = useWorkspaceText();
  // The home reads the project again each time it opens, so new chats and files are there.
  const { id, project, loading, error, refresh } = useProjectHome({ revalidate: true });
  const [tab, setTab] = useState<Tab>('all');
  if (!id || (!project && !error)) return loading && id ? <div aria-busy="true" /> : null;
  if (!project)
    return (
      <section className="ws-project-work">
        <p className="ws-project-empty" role="alert">
          {copy.projectLoadFailed}{' '}
          <button type="button" className="ws-text-button" onClick={() => void refresh()}>
            {uiText('Try again')}
          </button>
        </p>
      </section>
    );
  const items = project.items;
  const truncated = project.itemCount > items.length;
  const current = TABS.find((entry) => entry.id === tab) ?? TABS[0];
  const shown = current.kinds;
  const rows = shown ? items.filter((item) => shown.includes(item.kind)) : items;
  const key = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = TABS.findIndex((entry) => entry.id === tab);
    const next = TABS[(index + step + TABS.length) % TABS.length].id;
    setTab(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-tab="${next}"]`)?.focus();
  };
  return (
    <section className="ws-project-work" aria-labelledby="project-work-title">
      <div className="ws-project-work-head">
        <h2 id="project-work-title">{copy.inThisProject}</h2>
        {items.length > 0 && <ProjectAddMenu project={project} onChange={putProject} />}
      </div>
      {items.length ? (
        <>
          <div
            className="ws-project-tabs"
            role="tablist"
            aria-label={copy.showTabs}
            onKeyDown={key}
          >
            {TABS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                data-tab={entry.id}
                aria-selected={tab === entry.id}
                tabIndex={tab === entry.id ? 0 : -1}
                onClick={() => setTab(entry.id)}
              >
                {copy[entry.label]}{' '}
                {/* Past the newest items only All can be counted: the rest aren't loaded. */}
                {(!entry.kinds || !truncated) && (
                  <small>
                    {entry.kinds
                      ? items.filter((item) => entry.kinds!.includes(item.kind)).length
                      : project.itemCount}
                  </small>
                )}
              </button>
            ))}
          </div>
          <div className="ws-project-rows" role="tabpanel" aria-label={copy[current.label]}>
            {rows.map((item) => (
              <ProjectItemRow key={`${item.kind}:${item.id}`} item={item} project={project} />
            ))}
            {!rows.length && !truncated && current.empty && (
              <p className="ws-project-empty">{copy[current.empty]}</p>
            )}
            {truncated && (
              <p className="ws-project-empty">
                {formatCopy(copy.newestShown, { shown: items.length, count: project.itemCount })}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="ws-project-empty">
            <b>{copy.nothingHereYet}</b> {copy.homeEmptyHelp}
          </p>
          <div className="ws-project-first">
            <ProjectAddMenu project={project} onChange={putProject} first />
            {project.role !== 'viewer' && (
              <ProjectShareButton
                project={project}
                onChange={putProject}
                label={copy.shareWithPeopleDoingIt}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ProjectItemRow({ item, project }: { item: ProjectItem; project: ProjectDetail }) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const kind = itemKind(item, locale);
  const mine = item.own || (item.addedBy?.userId ?? null) === ws.user?.id;
  // Rule 9's screen shortcut: take work out (it stays where it lives), or move your own chat out.
  const out = async () => {
    const write = projectWriter();
    try {
      if (item.kind === 'chat') {
        await projectsApi.moveChat(item.id, null);
        await refreshProject(project.id);
      } else {
        const next = await projectsApi.removeItem(project.id, item.kind, item.id);
        if (next) write(next);
        else {
          dropProject(project.id);
          ws.openProject(null);
        }
      }
      ws.notify(
        formatCopy(copy.itemOutSuccess, { item: item.title, project: project.name }),
        null,
        'success',
      );
    } catch {
      ws.notify(
        formatCopy(copy.itemOutError, { item: item.title, project: project.name }),
        null,
        'error',
      );
    }
  };
  const actions: OverflowItem[] =
    item.kind === 'chat'
      ? item.own
        ? [{ key: 'out', label: copy.moveOutProject, onSelect: () => void out() }]
        : []
      : project.role !== 'viewer' || mine
        ? [{ key: 'out', label: copy.takeOutProject, onSelect: () => void out() }]
        : [];
  return (
    <div className="ws-project-row">
      <button
        type="button"
        className="ws-project-row-open"
        onClick={() => void openProjectItem(ws, item)}
      >
        <span className="ws-project-kind" aria-hidden="true">
          <WorkspaceIcon name={kind.icon} size={16} />
        </span>
        <span className="ws-project-row-text">
          <b>{item.title}</b>
          <small>{itemLine(item, ws.user?.id, locale)}</small>
        </span>
        <span className="ws-project-row-when">
          {item.live && <span className="ws-project-live" aria-hidden="true" />}
          {item.live ? copy.now : shortWhen(item.activityAt ?? item.updatedAt, locale)}
        </span>
      </button>
      {actions.length > 0 && (
        <span className="ws-project-row-menu">
          <OverflowMenu label={formatCopy(copy.moreFor, { name: item.title })} items={actions} />
        </span>
      )}
    </div>
  );
}

/** The top bar on a project's home: Projects / its name. */
export function ProjectHomeCrumbs() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const { project, place } = useProjectHome();
  const name = project?.name ?? place?.name ?? '';
  return (
    <>
      <span className="ws-project-crumbs">
        <button type="button" className="ws-project-crumb" onClick={() => ws.openProject(null)}>
          {copy.projects}
        </button>
        <span className="ws-slash">/</span>
        <strong>{name}</strong>
      </span>
      {/* A phone has room for one title: the project, under the word Project. */}
      <span className="ws-project-title">
        <small>
          {name && (
            <ProjectMark name={name} tint={project?.tint ?? place?.tint ?? 'plain'} size="xs" />
          )}
          {copy.project}
        </small>
        <strong>{name}</strong>
      </span>
    </>
  );
}

/** The top bar's right side on a project's home, as a chat has its own. */
export function ProjectHomeActions() {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const { id, project } = useProjectHome();
  const [deleting, setDeleting] = useState<'ask' | 'busy' | null>(null);
  if (!id || !project) return null;
  const actions: OverflowItem[] = [
    ...(project.role !== 'viewer'
      ? [
          {
            key: 'change',
            label: copy.changeInChat,
            icon: <WorkspaceIcon name="edit" size={16} />,
            description: copy.renameProjectDescription,
            onSelect: () => startProjectChat(ws, project, `${editor.changeProjectPrompt} `, false),
          },
        ]
      : []),
    ...(project.role === 'owner'
      ? [
          {
            key: 'delete',
            label: copy.deleteProject,
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setDeleting('ask'),
          },
        ]
      : []),
  ];
  const remove = async () => {
    setDeleting('busy');
    try {
      await projectsApi.remove(project.id);
      dropProject(project.id);
      ws.notify(formatCopy(copy.projectDeleted, { name: project.name }), null, 'success');
      ws.openProject(null);
    } catch {
      ws.notify(formatCopy(copy.couldntDelete, { name: project.name }), null, 'error');
      setDeleting('ask');
    }
  };
  return (
    <div className="ws-project-home-actions">
      <AboutProjectButton id={id} />
      {project.people.length > 1 && (
        <ProjectFaces
          people={project.people}
          max={3}
          label={project.people.map((person) => personName(person, locale)).join(', ')}
        />
      )}
      <ProjectShareButton project={project} onChange={putProject} />
      {actions.length > 0 && (
        <OverflowMenu label={formatCopy(copy.moreFor, { name: project.name })} items={actions} />
      )}
      {deleting && (
        <ConfirmDialog
          title={formatCopy(copy.deleteQuestion, { name: project.name })}
          description={copy.deleteDescription}
          confirmLabel={formatCopy(copy.deleteConfirm, { name: project.name })}
          busy={deleting === 'busy'}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void remove()}
        />
      )}
    </div>
  );
}
